import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Typography,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Row,
  Col,
  message,
  Popconfirm,
  Alert,
  Grid,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

interface LiveEvent {
  id: string;
  name: string;
  type: string;
  status: string;
  tileStartTime: string;
  tileEndTime: string;
  eventStartTime: string;
  eventEndTime: string;
  platform: string;
  targetCountries: string;
  participantCount?: number;
  matchCount?: number;
  createdAt: string;
}

const statusColors: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  tile_visible: 'cyan',
  active: 'green',
  completed: 'default',
  cancelled: 'red',
};

const typeLabels: Record<string, string> = {
  world_cup: 'World Cup',
  ipl: 'IPL',
  t20_league: 'T20 League',
  test_series: 'Test Series',
  special_event: 'Special Event',
};

export const LiveEventList: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [apiAvailable, setApiAvailable] = useState(true);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {};
      if (searchText) params.search = searchText;
      if (statusFilter) params.status = statusFilter;

      const response = await adminApi.getLiveEvents(params);
      const data = response.data?.data || response.data;
      setEvents(Array.isArray(data) ? data : []);
      setApiAvailable(true);
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; code?: string };
      if (err.response?.status === 404 || err.code === 'ERR_NETWORK') {
        setApiAvailable(false);
      } else {
        message.error('Failed to fetch live events');
      }
      setEvents([]); // Ensure empty array on error
    } finally {
      setLoading(false);
    }
  }, [searchText, statusFilter]);

  useEffect(() => {
    fetchEvents();
  }, [statusFilter, fetchEvents]);

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteLiveEvent(id);
      message.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      message.error('Failed to delete event');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: LiveEvent) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {typeLabels[record.type] || record.type}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <Tag color={statusColors[status]}>{status?.toUpperCase()}</Tag>,
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: string) => <Tag>{platform?.toUpperCase()}</Tag>,
    },
    {
      title: 'Tile Window',
      key: 'tileWindow',
      render: (_: unknown, record: LiveEvent) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            {new Date(record.tileStartTime).toLocaleDateString()}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            to {new Date(record.tileEndTime).toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Event Window',
      key: 'eventWindow',
      render: (_: unknown, record: LiveEvent) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            {new Date(record.eventStartTime).toLocaleDateString()}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            to {new Date(record.eventEndTime).toLocaleDateString()}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Participants',
      dataIndex: 'participantCount',
      key: 'participantCount',
      render: (count: number) => count?.toLocaleString() || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: LiveEvent) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/live-events/${record.id}`)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/live-events/${record.id}/edit`)}
          />
          <Popconfirm
            title="Delete this event?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!apiAvailable) {
    return (
      <div>
        <div className="page-header">
          <Title level={3}>
            <TrophyOutlined /> Live Events
          </Title>
          <Text type="secondary">
            Manage live tournament events tied to real-world cricket matches
          </Text>
        </div>
        <Alert
          message="Feature Not Available"
          description="The live events admin API endpoints are not yet implemented on the backend. This feature will be available once the backend support is added."
          type="info"
          showIcon
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <Title level={3}>
          <TrophyOutlined /> Live Events
        </Title>
        <Text type="secondary">
          Manage live tournament events tied to real-world cricket matches
        </Text>
      </div>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Search events..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={fetchEvents}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { label: 'Draft', value: 'draft' },
                { label: 'Scheduled', value: 'scheduled' },
                { label: 'Tile Visible', value: 'tile_visible' },
                { label: 'Active', value: 'active' },
                { label: 'Completed', value: 'completed' },
                { label: 'Cancelled', value: 'cancelled' },
              ]}
            />
          </Col>
          <Col xs={24} sm={24} md={10} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/live-events/create')}
            >
              Create Event
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={events}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          size={screens.xs ? 'small' : 'middle'}
          pagination={{ pageSize: 10, size: 'small' }}
        />
      </Card>
    </div>
  );
};

export default LiveEventList;
